
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FloorPlanPreview } from "@/components/dashboard/FloorPlanPreview";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { UserHeader } from "./floor-plan/UserHeader";
import { RatingStars } from "./floor-plan/RatingStars";
import { CommentsDialog } from "./floor-plan/CommentsDialog";
import { PreviewDialog } from "./floor-plan/PreviewDialog";
import { copyAndActivateSharedFloorPlan } from "@/services/floorPlanService";

interface SharedFloorPlanCardProps {
  plan: {
    id: string;
    name: string;
    preview_image: string | null;
    updated_at: string;
    content: any;
    user_id: string;
    user: {
      id: string;
      display_name: string | null;
      username: string | null;
      avatar_url: string | null;
    } | null;
    average_rating?: number | null;
    ratings_count?: number | null;
  };
}

export function SharedFloorPlanCard({ plan }: SharedFloorPlanCardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [averageRating, setAverageRating] = useState(plan.average_rating || 0);
  const [totalRatings, setTotalRatings] = useState(plan.ratings_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  React.useEffect(() => {
    fetchRatings();
  }, [plan.id]);

  const fetchRatings = async () => {
    const { data: ratings, error } = await supabase
      .from('floor_plan_ratings')
      .select('rating')
      .eq('floor_plan_id', plan.id);

    if (error) {
      console.error('Error fetching ratings:', error);
      return;
    }

    if (ratings.length > 0) {
      const avg = ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length;
      setAverageRating(avg);
      setTotalRatings(ratings.length);
    }

    if (user) {
      const { data: userRating, error: userRatingError } = await supabase
        .from('floor_plan_ratings')
        .select('rating')
        .eq('floor_plan_id', plan.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (userRatingError) {
        console.error('Error fetching user rating:', userRatingError);
        return;
      }

      if (userRating) {
        setRating(userRating.rating);
      }
    }
  };

  const handleRate = async (value: number) => {
    if (!user) {
      toast({
        title: "Niet ingelogd",
        description: "Je moet ingelogd zijn om een beoordeling te geven.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if user already rated this floor plan
      const { data: existingRating, error: checkError } = await supabase
        .from('floor_plan_ratings')
        .select('id, rating')
        .eq('floor_plan_id', plan.id)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (checkError) {
        console.error('Error checking existing rating:', checkError);
        throw checkError;
      }

      if (existingRating) {
        // If rating is the same, inform user
        if (existingRating.rating === value) {
          toast({
            title: "Beoordeling ongewijzigd",
            description: "Je hebt deze plattegrond al beoordeeld met dezelfde score.",
          });
          return;
        }
        
        // Update existing rating
        const { error: updateError } = await supabase
          .from('floor_plan_ratings')
          .update({ rating: value })
          .eq('id', existingRating.id);
        
        if (updateError) throw updateError;
        
        toast({
          title: "Beoordeling bijgewerkt",
          description: "Je beoordeling is succesvol bijgewerkt.",
        });
      } else {
        // Create new rating
        const { error: insertError } = await supabase
          .from('floor_plan_ratings')
          .insert({
            floor_plan_id: plan.id,
            user_id: user.id,
            rating: value,
          });
        
        if (insertError) throw insertError;
        
        toast({
          title: "Beoordeling opgeslagen",
          description: "Bedankt voor je beoordeling!",
        });
      }

      setRating(value);
      fetchRatings(); // Refresh ratings to update UI
    } catch (error: any) {
      console.error('Error saving rating:', error);
      toast({
        title: "Fout bij opslaan",
        description: "Er is een fout opgetreden bij het opslaan van je beoordeling. Probeer het later nog eens.",
        variant: "destructive",
      });
    }
  };

  const handleCopy = async () => {
    if (!user) {
      toast({
        title: "Niet ingelogd",
        description: "Je moet ingelogd zijn om een plattegrond te kopiëren.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCopying(true);
      
      const newPlan = await copyAndActivateSharedFloorPlan(plan, user.id);

      toast({
        title: "Plattegrond gekopieerd",
        description: "De plattegrond is succesvol gekopieerd naar jouw account.",
      });

      navigate(`/?id=${newPlan.id}`);
    } catch (error) {
      console.error('Error copying floor plan:', error);
      toast({
        title: "Fout bij kopiëren",
        description: "Er is een fout opgetreden bij het kopiëren van de plattegrond.",
        variant: "destructive",
      });
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <UserHeader user={plan.user ? { ...plan.user, id: plan.user_id } : null} updated_at={plan.updated_at} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <FloorPlanPreview
            id={plan.id}
            name={plan.name}
            previewImage={plan.preview_image}
            allowRefresh={false}
          />
        </div>
        <h3 className="font-medium">{plan.name}</h3>
        
        <RatingStars
          rating={rating}
          averageRating={averageRating}
          totalRatings={totalRatings}
          onRate={handleRate}
          showAverageStars={true}
        />

        <CommentsDialog
          floorPlanId={plan.id}
          open={showComments}
          onOpenChange={setShowComments}
        />

        <PreviewDialog
          plan={plan}
          open={showPreview}
          onOpenChange={setShowPreview}
        />

        <Button 
          onClick={handleCopy}
          className="w-full"
          variant="outline"
          disabled={isCopying}
        >
          <Copy className="mr-2 h-4 w-4" />
          {isCopying ? "Bezig met kopiëren..." : "Kopieer naar mijn plattegronden"}
        </Button>
      </CardContent>
    </Card>
  );
}
